/* ============================================================
   useAuth — who is listening, and how they got in.

   The rules already exist and are tested: session.js decides what a pass link means and who the listener is,
   real.js/mock.js own the OAuth round trip, memory.js owns the sweep. This store is where the app asks them, once,
   in an order that matters:

     1. a pass link (?token=…) is taken out of the address BEFORE anything reads it — the token never enters history
     2. finishLogin() runs before anything asks who the user is (it is the only reader of ?code/?state)
     3. only then does /me get asked — by the room's finish after a round trip or a pass, by the room itself beside
        its restore otherwise (askMe), by a share page after its quiet finish; the front door and the legal pages ask
        nothing (boot's `page`)

   `ready` is false until that has happened. Every view that cares waits on it rather than guessing — the old site
   could get away with a boot script, an SPA cannot.
   ============================================================ */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api, MOCK } from '@/api/index.js';
import { getAccess, clearTokens } from '@/api/token.js';
import { K, ROOM_PREFIXES } from '@/utils/keys.js';
import { passLink, firstName, uidOf } from './session.js';
import { jwtSub, firstTimeHere } from './pass.js';
import { retryMe } from '@/utils/retry-me.js';
import { ENV } from '@/api/env.js';
import { track, trackLogin, trackLogout } from '@/api/track.js';

export interface User {
  user_id?: string;
  name?: string;
  handle?: string;
  avatar?: string;
  [k: string]: unknown;
}

/* what the sign-in round trip said, for the view that has to speak about it */
export interface Landing {
  ok: boolean;
  resume: unknown;
  error?: string;
  is_new?: boolean;
}

export const useAuth = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const ready = ref(false);
  const landing = ref<Landing | null>(null);
  /* the session died under us (a 401 on a live room): the door opens without a reload */
  const dead = ref(false);
  /* THE FIRST NIGHT: the first time this token's subject reaches the room on this browser. Read ONCE, here,
     because reading it is what spends it — `firstTimeHere` writes the mark as it answers. Google's round trip
     has the server's `is_new`, but a pass has no such thing, so the browser's own memory is the one answer both
     doors can give. It is what makes her speak first (thread.greet). */
  const firstNight = ref(false);

  /* the api's session is not reactive (tokens in storage, the mock's user in its module), so what `authed` read is
     re-read whenever the session may have moved under it — a pass taken, a round trip finished, a sign-out. The room
     stands before its finish (RoomView), so a guest room's answer must not outlive the tokens the finish saves. */
  const session = ref(0);
  const authed = computed(() => (session.value, !!user.value || api.isAuthed()));
  const who = computed(() => firstName(user.value));
  const meId = computed(() => String(user.value?.user_id || ''));
  const uid = computed(() => uidOf({ mock: MOCK, sub: jwtSub(getAccess()), meId: meId.value }));

  /** Take a pass link out of the address. Runs before anything else reads `location`. */
  function takePassLink() {
    const pass = passLink(location.search, { passLink: ENV.PASS_LINK });
    if (!pass) return false;
    if (pass.token) {
      localStorage.setItem(K.ACCESS, pass.token);
      localStorage.removeItem(K.REFRESH); /* a pass has no refresh: when it runs out the 401 opens the door */
    }
    /* only the token leaves the address — a ?code/?state riding along must still be read by finishLogin */
    history.replaceState(null, '', location.pathname + pass.search + location.hash);
    return !!pass.token;
  }
  /* THE PASS LINK IS TAKEN FIRST (listen.js:108-118, a block at the top of the page, before any of the room is made): the
     room's setup takes it before its models exist — the playlists' boot asks /client/playlists as it is made, and a
     request that goes out bare comes back 401 "Missing bearer token", which clears the session (http.js), the pass
     kept a moment later with it. Once per document; the finish reads what was taken. */
  let passTaken: boolean | null = null;
  function takePass() {
    if (passTaken === null) {
      passTaken = takePassLink();
      session.value++; /* `authed` reads the session again */
    }
    return passTaken;
  }

  let booting: Promise<void> | null = null;
  /** Idempotent: every caller awaits the same boot, and the first one — the document's own page — decides what it does,
   *  as each page of the reference ran its own:
   *    'room'   listen.js: THE ROUND TRIP IS THE ROOM'S TO FINISH (`await signin.finish()`; the door in index.html
   *             forwards any "/" carrying one to /listen) — a pass link, a Google code, a trip still pending
   *    'share'  song.js / playlist.js: a trip that still lands here is stale — finished quietly, then who is listening
   *    'quiet'  front.js, terms.html, privacy.html: nothing asked (the door forwards a member to the room first) */
  function boot({ page = 'quiet' }: { page?: 'room' | 'share' | 'quiet' } = {}) {
    if (!booting) booting = run(page);
    return booting;
  }

  async function run(page: 'room' | 'share' | 'quiet') {
    if (page === 'room') await finishRoom();
    else if (page === 'share') await finishShare();
    ready.value = true;
  }

  /* a pass link taken at this document's boot: boot's /me proved it, so the room does not ask again (listen.js
     `if (api.isAuthed() && !passArrived) meReady = …`) */
  let passArrived = false;

  async function finishRoom() {
    passArrived = takePass();
    try {
      landing.value = (await api.finishLogin()) as Landing | null;
    } catch {
      landing.value = null; /* a broken round trip is not a broken room: the door can be opened again */
    }
    session.value++; /* a pass taken, tokens saved: `authed` reads the session again */
    const fin = landing.value;
    const resume = (fin?.resume || null) as { kind?: string; back?: string; which?: string } | null;
    /* THE SONG PAGE'S ROUND TRIP (js/ui/desktop/door.js finish): a song's or a video's own page, carried by the
       login's resume from the landing's &back=, is where the visitor goes — straight back, before the room paints.
       What boots on meanwhile is what the reference's room did in the moment before the page went. */
    const back =
      fin?.ok && resume?.kind === 'song' && /^\/(songs|videos)\/[^/?#]+$/.test(resume.back || '') ? resume.back! : '';
    if (back) {
      track('webLoginEnd', {
        which_source: resume!.which || (back.startsWith('/videos/') ? 'video' : 'song'),
        is_new: !!fin!.is_new,
      });
      location.replace(back);
      return;
    }
    if (!api.isAuthed()) return;
    /* WHO SIGNED IN, AND HOW (listen.js signedIn and its callers). A round trip that landed and a pass that arrived
       SIGN IN: the session is alive again, the analytics' identity is this listener's, and the first night is decided
       — the server's is_new for the round trip (Google knows), this browser's first-time mark for a pass (it has no
       is_new; asking the mark is what spends it). A session this browser already held asks nothing here: the room
       asks who is listening itself, beside its restore (askMe). */
    if (fin?.ok) {
      /* null: signed in on paper, unreachable in fact — the room opens as a guest and says so. After a round trip the
         profile gets three asks, 700 ms apart (door.js finish): the tokens are saved, a flake is a profile loading */
      const me = (await retryMe(() => api.me())) as User | null;
      /* a round trip that landed is counted once the profile has been asked for — whatever it said — and before
         the identify (door.js finish) */
      track('webLoginEnd', { which_source: resume?.which || 'listen', is_new: !!fin.is_new });
      user.value = me;
      if (me) signedIn(me, !!fin.is_new);
    } else if (passArrived) {
      const me = ((await api.me().catch(() => null)) as User | null) || null;
      user.value = me;
      if (me) signedIn(me, firstTimeHere(jwtSub(getAccess()) || me.user_id));
    }
  }
  function signedIn(me: User, isNew: boolean) {
    dead.value = false;
    trackLogin(me.user_id);
    firstNight.value = isNew;
  }

  /* A SHARE PAGE'S BOOT (song.js / playlist.js): the sign-in itself happens at the front door's door, so a trip that
     still lands here is stale — take the win if there is one, say nothing (no webLoginEnd, no redirect, no first
     night). Then who is listening, if anyone; setUser identifies them whatever the mode. A throw ends it, as there. */
  async function finishShare() {
    const setUser = (u: User) => {
      user.value = u;
      trackLogin(u.user_id);
    };
    try {
      const fin = (await api.finishLogin()) as Landing | null;
      session.value++;
      if (fin?.ok) {
        const u = (await api.me().catch(() => null)) as User | null;
        if (u) setUser(u);
      }
      if (!user.value && api.isAuthed()) {
        const u = (await api.me().catch(() => null)) as User | null;
        if (u) setUser(u);
      }
    } catch {
      /* the page opens for a stranger */
    }
  }

  /* THE ROOM'S OWN /me (listen.js meReady): asked beside its restore, before the thread's round trip — a session held,
     or a second ask after a round trip that landed (its finish fetched the profile already). A pass that just arrived
     was proven by boot's, once: a room that comes back in this document asks, as its next document would have. The first
     message and her first words wait for it — the name rides the first snapshot. */
  let meReady: Promise<void> = Promise.resolve();
  function askMe() {
    if (passArrived) {
      passArrived = false;
      return meReady;
    }
    if (!api.isAuthed()) return meReady;
    meReady = (api.me() as Promise<User | null>)
      .then((u) => {
        if (u) {
          user.value = u;
          if (!MOCK) trackLogin(u.user_id);
        }
      })
      .catch(() => {});
    return meReady;
  }
  const whenMe = () => meReady;

  /** Leave for the identity provider. The page does not come back — the caller is parked. */
  function signIn(resume?: unknown, inviteCode?: string) {
    return api.login(resume ?? null, inviteCode || '');
  }

  /**
   * Sign out. The tokens go FIRST and locally (real.js clears them only after the server answers, and whatever
   * happens next must not find them), then everything this account left in this browser.
   * `forget` also drops the first-night memory, so the next sign-in is a first night again.
   */
  async function signOut({ forget = false } = {}) {
    const sub = jwtSub(getAccess()) || meId.value;
    clearTokens();
    session.value++;
    localStorage.removeItem(K.SESSION);
    sweepRoomKeys();
    if (forget) {
      try {
        localStorage.removeItem(K.seen(sub));
        localStorage.removeItem(K.MOCK_SEEN);
      } catch {
        /* a browser that refuses storage has nothing to forget */
      }
    }
    user.value = null;
    dead.value = false;
    trackLogout();
    try {
      await api.logout();
    } catch {
      /* the server's copy is best-effort: this browser is already clean */
    }
  }

  /** Every room key, by prefix — the same sweep memory.forget does, for the keys written outside it. */
  function sweepRoomKeys() {
    try {
      const doomed: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && ROOM_PREFIXES.some((p) => k.startsWith(p))) doomed.push(k);
      }
      for (const k of doomed) localStorage.removeItem(k);
    } catch {
      /* private mode: nothing was written, nothing to sweep */
    }
  }

  return {
    user,
    ready,
    landing,
    dead,
    firstNight,
    authed,
    who,
    uid,
    meId,
    boot,
    takePass,
    askMe,
    whenMe,
    signIn,
    signOut,
    sweepRoomKeys,
  };
});
